/**
 * <OwnerPill /> — compact display of the IdP-resolved owner of an
 * agent, device, or alert. Defers resolver fetch until the pill is
 * visible (IntersectionObserver). Renders graceful fallbacks for
 * loginwindow / empty consoleUser / 404 / unknown confidence.
 *
 * Built only on existing primitives (Avatar, HoverCard, Tooltip,
 * Skeleton). Click opens an OwnerDrawer unless `inert`.
 */
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useResolvePrincipal } from "@/hooks/security/useResolvePrincipal";
import { useInView } from "@/hooks/security/useLighthouseAgentOwner";
import type {
  IdPPrincipalDTO,
  PrincipalConfidence,
} from "@/types/lighthouseApi";
import { OwnerDrawer } from "./OwnerDrawer";

export interface OwnerPillProps {
  /** Raw console user string from EDR/Lighthouse payload. */
  consoleUser?: string | null;
  /** Pre-resolved principal — skips the resolver fetch when present. */
  principal?: IdPPrincipalDTO | null;
  deviceId?: string;
  tenantId?: string;
  size?: "sm" | "md";
  /** Render-only (no hover card, no click target). */
  inert?: boolean;
  /** Render a skeleton while loading instead of an em dash. */
  showSkeleton?: boolean;
  className?: string;
}

const CONFIDENCE_DOT: Record<PrincipalConfidence, string> = {
  high: "bg-success",
  medium: "bg-warning",
  low: "bg-muted-foreground",
  inferred: "bg-muted-foreground",
  unknown: "bg-muted-foreground/40",
};

function initials(name?: string, email?: string): string {
  const src = (name || email || "").trim();
  if (!src) return "?";
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

export function OwnerPill({
  consoleUser,
  principal: preResolved,
  deviceId,
  tenantId: _tenantId,
  size = "md",
  inert = false,
  showSkeleton = false,
  className,
}: OwnerPillProps) {
  const { ref, inView } = useInView<HTMLSpanElement>("200px");
  const trimmed = (consoleUser ?? "").trim();
  const isLoginWindow = trimmed.toLowerCase() === "loginwindow";
  const hasInput = trimmed.length > 0 && !isLoginWindow;
  const shouldFetch = !preResolved && hasInput && inView;

  const query = useResolvePrincipal(consoleUser, {
    deviceId,
    enabled: shouldFetch,
  });

  const principal: IdPPrincipalDTO | null | undefined =
    preResolved ?? query.data;

  const [open, setOpen] = useState(false);

  const sizes =
    size === "sm"
      ? { avatar: "h-4 w-4 text-[9px]", text: "text-xs", dot: "h-1.5 w-1.5" }
      : { avatar: "h-6 w-6 text-[10px]", text: "text-sm", dot: "h-2 w-2" };

  // --- Empty / loginwindow state ---
  if (!hasInput && !preResolved) {
    const tip = isLoginWindow ? "No console user signed in" : "No console user";
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              ref={ref}
              className={cn(
                "inline-flex items-center text-muted-foreground/60",
                sizes.text,
                className,
              )}
            >
              —
            </span>
          </TooltipTrigger>
          <TooltipContent>{tip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // --- Loading state ---
  const isLoading = !preResolved && (query.isLoading || (shouldFetch && !query.data && !query.isError));
  if (isLoading) {
    if (showSkeleton) {
      return (
        <span
          ref={ref}
          className={cn("inline-flex items-center gap-1.5", className)}
        >
          <Skeleton className={cn("rounded-full", sizes.avatar)} />
          <Skeleton className="h-3 w-20" />
        </span>
      );
    }
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center text-muted-foreground/60",
          sizes.text,
          className,
        )}
      >
        —
      </span>
    );
  }

  // --- Unattributed (404 / unknown) ---
  const confidence: PrincipalConfidence = (principal?.confidence as PrincipalConfidence) ?? "unknown";
  const resolved = principal && confidence !== "unknown";

  if (!resolved) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              ref={ref}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border border-dashed border-border bg-muted/30 px-1.5 py-0.5 text-muted-foreground",
                sizes.text,
                className,
              )}
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
              <span>Unattributed</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            No IdP binding for <span className="font-mono">{trimmed}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // --- Resolved state ---
  const name = principal!.displayName || principal!.email || trimmed;
  const pill: ReactNode = (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border bg-card/50 px-1.5 py-0.5 transition-colors",
        !inert && "hover:bg-accent/40 cursor-pointer",
        sizes.text,
        className,
      )}
      onClick={inert ? undefined : () => setOpen(true)}
      role={inert ? undefined : "button"}
      tabIndex={inert ? undefined : 0}
      onKeyDown={
        inert
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen(true);
              }
            }
      }
    >
      <Avatar className={sizes.avatar}>
        {principal!.avatarUrl ? (
          <AvatarImage src={principal!.avatarUrl} alt={name} />
        ) : null}
        <AvatarFallback className="bg-primary/15 text-primary uppercase">
          {initials(principal!.displayName, principal!.email)}
        </AvatarFallback>
      </Avatar>
      <span className="truncate max-w-[180px] text-foreground">{name}</span>
      <span
        className={cn("inline-block rounded-full", sizes.dot, CONFIDENCE_DOT[confidence])}
        aria-label={`${confidence} confidence`}
      />
    </span>
  );

  if (inert) return <>{pill}</>;

  return (
    <>
      <HoverCard openDelay={200} closeDelay={120}>
        <HoverCardTrigger asChild>{pill}</HoverCardTrigger>
        <HoverCardContent className="w-72" align="start">
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10">
              {principal!.avatarUrl ? (
                <AvatarImage src={principal!.avatarUrl} alt={name} />
              ) : null}
              <AvatarFallback className="bg-primary/15 text-primary uppercase">
                {initials(principal!.displayName, principal!.email)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground truncate">
                {name}
              </div>
              {principal!.email && (
                <div className="text-xs text-muted-foreground truncate">
                  {principal!.email}
                </div>
              )}
              {(principal!.title || principal!.department) && (
                <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                  {[principal!.title, principal!.department]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Stat
              label="Devices"
              value={principal!.primaryDeviceCount ?? "—"}
            />
            <Stat
              label="Agents"
              value={principal!.agentsBoundCount ?? "—"}
            />
            <Stat
              label="IdP"
              value={principal!.idpProvider ?? "—"}
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <span
                className={cn(
                  "inline-block h-1.5 w-1.5 rounded-full",
                  CONFIDENCE_DOT[confidence],
                )}
              />
              {confidence} confidence
            </span>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => setOpen(true)}
            >
              View profile
            </button>
          </div>
        </HoverCardContent>
      </HoverCard>
      <OwnerDrawer
        open={open}
        onOpenChange={setOpen}
        principal={principal!}
      />
    </>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-2 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-xs font-medium text-foreground truncate">
        {value}
      </div>
    </div>
  );
}
